import User from '@/models/user/User';
import {ProductType} from '@/commons/enums/ProductType';
import {Privileges} from '@/models/user/Privileges';
import {UserDivisionStatus} from '@/models/user/UserDTO';
import {UserListElement} from '@/models/ppl/UserListElement';
import {Quizzes} from '@/models/user/Quizzes';
import EnvironmentService from '@/env/EnvironmentService';
import { LendiCareInfo } from '@/models/user/LendiCare';

export default class FinCRM_User {
  id!: number;

  location: object = {};
  accesses: Accesses = new Accesses();
  activity: Activity = new Activity();

  firstName!: string;
  lastName!: string;
  email!: string;
  phone!: string;
  pesel!: string;
  nip!: string;
  url?: string;
  newPassword?: string;
  defaultPartnerId?: number;
  isRodoAvailable: boolean = false;
  organisation: Organisation = new Organisation();
  isActive: boolean = true;
  hasUserAccount: boolean = true;
  userName: string = '';
  isChiefOfDivision: boolean = false;
  divisionsChief?: Array<UserDivisionStatus>;
  lendiCareInfo?: LendiCareInfo

  failedLoginAttemptsCount: number = 0;

  constructor(user?: FinCRM_User | UserListElement | User, privileges?: Privileges, validUntil?: Date,
    failedLoginAttempt?: number, requirePasswordChange?: boolean, unpaidAccount?: boolean,
    divisionsIds?: Array<number>,quiz?: Quizzes) {
    if (user instanceof FinCRM_User) {
      Object.assign(this, user);
    } else if (user instanceof UserListElement || user instanceof User) {
      this.id = user.id;
      this.firstName = user.firstName!;
      this.lastName = user.lastName!;
      this.userName = user.username!;
      this.email = user.email!;
      this.phone = user.phone!;
      this.pesel = user.pesel!;
      this.accesses.flags.isChief = user.isChief;
      this.organisation.id = user.organisationId;
      if (user instanceof User) {
        this.accesses.flags.forceLogout = user.forceLogout;
        this.defaultPartnerId = user.defaultPartnerId;
        this.accesses.flags.expiring = user.isExpired;
        this.accesses.flags.requireBankSecrecy = user.requireBankSecrecy;
        this.isChiefOfDivision = user.isChiefOfDivision;
        this.organisation.unitId = user.organisationUnitId;
        this.divisionsChief = user.divisionsChief;
      }
    }
    if (privileges) {
      this.isRodoAvailable = privileges.isRodoAvailable;
      this.accesses.flags.hasAccessToRaiffeisen = privileges.hasAccessToRaiffeisen;
      this.accesses.flags.requireAccreditation = privileges.requireAccreditation;
      this.accesses.flags.isHrLocked = privileges.isHrLocked;
      this.accesses.products.mortgage = privileges.mortgageLoansDisabled || false;
      this.accesses.products.cash = privileges.cashLoansDisabled || false;
      this.accesses.products.sme = privileges.smeLoansDisabled || false;
      this.accesses.experts = privileges.hasAccessToExperts;
      this.accesses.flags.hasFilemanagerAccess = privileges.hasFilemanagerAccess;
      this.accesses.flags.canSkipVerification = privileges.canSkipVerification;
      this.accesses.flags.canManageProspects = privileges.canManageProspects;
      this.accesses.flags.canMeetOutsideOffice = privileges.canMeetOutsideOffice;
      this.accesses.flags.isAgentCC = privileges.isAgentCC;
      this.accesses.flags.canManageCash = privileges.canManage[ProductType.CASH];
      this.accesses.flags.canManageMortgage = privileges.canManage[ProductType.MORTGAGE];
      this.accesses.flags.canManageSme = privileges.canManage[ProductType.SME];
      this.accesses.flags.canManageRealEstate = privileges.canManage[ProductType.REAL_ESTATE];
      this.accesses.flags.canManageInsurance = privileges.canManage[ProductType.INSURANCE];
      this.accesses.flags.simplePartnerView = privileges.simplePartnerView;
      this.accesses.provisions = privileges.canEditProvisions;
      this.accesses.flags.canManageWidgets = privileges.canManage.widgets;
      this.accesses.flags.isBigAdmin = privileges.isBigAdmin;
      this.accesses.flags.isProductAdmin = privileges.isProductAdmin;
      this.accesses.flags.isBokAdmin = privileges.isBokAdmin;
      this.accesses.flags.canManageOrganisation = privileges.canManageOrganisation;
      this.accesses.flags.hasAccessToLendiCareLead = privileges.hasAccessToLendiCareLead;
      this.accesses.flags.hasAccessToLendiCare = privileges.hasAccessToLendiCare;
    }
    if (quiz) {
      this.accesses.flags.requireOsh = quiz.requireOsh;
      this.accesses.flags.requireKnf = quiz.requireKnf;
      this.accesses.flags.requireRodo = quiz.requireRodo;
      this.accesses.flags.requireAml = quiz.requireAml;
      this.accesses.flags.requireCyber = quiz.requireCyber;
      this.accesses.flags.requireStmt = quiz.requireStmt;
      this.accesses.flags.requireEthic = quiz.requireEthic
    }
    if (typeof requirePasswordChange === 'boolean') {
      this.accesses.flags.requirePasswordChange = requirePasswordChange;
    }
    this.failedLoginAttemptsCount = failedLoginAttempt || 0;
    if (typeof unpaidAccount === 'boolean') {
      this.accesses.flags.unpaidAccount = unpaidAccount;
    }
    if (divisionsIds) {
      this.organisation.divisionIds = divisionsIds;
    }
    if (validUntil) {
      this.activity.until = validUntil;
    }
    this.activity.created = new Date(); // Tutaj potrzeba daty stworzenia, co prawda podczas edycji nie jest zmieniana ale endpoint jej chce
  }

  public get name() {
    return this.firstName + ' ' + this.lastName;
  }
}

class Accesses {
  products: Products = new Products();
  provisions: boolean = false;
  experts: boolean = true;
  flags: Flags = new Flags();
}

class Products {
  mortgage: boolean = false;
  cash: boolean = false;
  sme: boolean = false;
}

class Flags {
  hasAccessToRaiffeisen: boolean = false;
  hasAccessToNest: boolean = false;
  hasAccessToSmartney: boolean = false;
  isProductAdmin: boolean = false;
  hasAccessToCpi: boolean = false;
  requirePasswordChange: boolean = true;
  isBigAdmin: boolean = false;
  isBokAdmin: boolean = false;
  expiring?: boolean = false;
  forceLogout: boolean = false;
  canManageOrganisation: boolean = false;
  unpaidAccount: boolean = true;
  hasFilemanagerAccess: boolean = true;
  hasAccessToSalaryLevels: boolean = false;
  canSkipVerification: boolean = true;
  canManageProspects: boolean = false;
  canMeetOutsideOffice: boolean = false;
  isAgentCC: boolean = false;
  requireAccreditation: boolean = false;
  canManageInsurance: boolean = false;
  canManageWidgets: boolean = false;
  canManageCash: boolean = true;
  canManageMortgage: boolean = true;
  canManageSme: boolean = true;
  canManageRealEstate: boolean = false;
  isChief?: boolean = false;
  simplePartnerView: boolean = false;
  requireOsh: boolean = false;
  requireKnf: boolean = false;
  isHrLocked: boolean = EnvironmentService.Environment.getAppDomainConfig().hasHrLock;
  requireBankSecrecy?: boolean = false;
  failedLoginAttempts?: boolean;
  requireRodo: boolean = false;
  requireAml: boolean = false;
  requireCyber: boolean = false;
  requireEthic: boolean = false;
  hasAccessToLendiCare: boolean = false;
  hasAccessToLendiCareLead: boolean = false;
  requireStmt: boolean = false;
}

export class Organisation {
  id?: number;
  name?: string;
  unitId?: number;
  divisionIds: Array<number> = [];
  lendicareSalesNetworkId?: number;
  lendicareDivisionPartnerId?: number;
}

class Activity {
  created?: Date;
  until?: Date;
}

// Stworzone na potrzeby stawek prowizyjnych, stawki prowizyjne
export class CommissionRates {
  id!: number;
  name!: string;
}
